import React, { useCallback, useState } from 'react';

import { Checkbox } from 'antd';
import { RawValueType } from 'rc-select/lib/BaseSelect';
import { LabelInValueType } from 'rc-select/lib/Select';

import {
  BrancheDtoBrancheEnum,
  DossierBodyResponseDto,
  DossierDtoAdminFonctionnel,
  DossierResponseDto,
  EtatDto,
  MotifMefDto,
  ReferentielControllerApi,
  UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import AsyncComponent from 'components/AsyncComponent/AsyncComponent';
import BaseForm from 'components/BaseForm/BaseForm';
import BaseInput from 'components/BaseForm/BaseInput';
import BaseTextArea from 'components/BaseForm/BaseTextArea';
import AsyncSelect from 'components/BaseForm/Select/AsyncSelect';
import useSubmitUpdateDonneesVehicule, {
  buildUpdateDonneesVehiculeDto,
  ControllerApi,
} from 'hooks/useSubmitUpdateDonneesVehicule';
import { fetchEtatsVehicule } from 'search/searchEtatsVehicule';
import { fetchMotifMefList } from 'search/searchMotifMef';
import { UpdateDonnesVehiculeFormValues } from 'types/DonneesVehicule/UpdateDonnesVehiculeFormValues';
import { etatDtoToString, formatMotifMefDto } from 'utils/dtoToString';

import BaseDatePicker from '../BaseForm/BaseDatePicker';
import { BaseOptionType } from '../BaseForm/Select/CommonBaseSelect';
import { FormInputProps } from '../BaseForm/types';

import useInitialValues from './useInitialValues';

type SetDossierAdmin = (dossier: DossierDtoAdminFonctionnel) => void;
type SetDossierFo = (dossier: DossierResponseDto) => void;

interface UpdateDonneesVehiculeProps {
  closeModal: () => void;
  controller: ControllerApi;
  dossierId: string;
  setDossier: SetDossierFo | SetDossierAdmin;
  refController: ReferentielControllerApi;
  fetchInProgress: boolean;
  fetchDossier: () => void;
  errorOccured: boolean;
  dossierBody: DossierBodyResponseDto | null | undefined;
  motifMef?: MotifMefDto;
}

function UpdateDonneesVehicule({
  closeModal,
  controller,
  dossierId,
  setDossier,
  refController,
  fetchInProgress,
  fetchDossier,
  errorOccured,
  dossierBody,
  motifMef,
}: UpdateDonneesVehiculeProps): React.ReactElement {
  const initialValues = useInitialValues(dossierBody);

  const [isPlaqueChanged, setPlaqueChanged] = useState(false);
  const [isMefProlongee, setIsMefProlongee] = useState(false);

  const initialPlaqueImmatriculation =
    initialValues.plaqueImmatriculation ?? '';

  const warningDetails = [
    'Les données de la fiche descriptive seront modifiées et conduiront à une nouvelle consultation du SIV, à un nouveau classement et à une nouvelle notification le cas échéant',
  ];

  if (isMefProlongee) {
    warningDetails.unshift(
      'Les données de la fiche descriptive seront modifiées et impacteront le traitement du dossier : changement de branche, interrogation SIV, classement et notification le cas échéant.',
    );
  }

  if (isPlaqueChanged) {
    warningDetails.unshift(
      'Attention : Les doublons de plaque d’immatriculation ne seront pas contrôlés lors de cette modification.',
    );
    if (initialValues.interdictionCirculer) {
      warningDetails.unshift(
        `L'IC ajoutée sur ${initialPlaqueImmatriculation} ne sera pas annulée par le SIF. Veuillez contacter les administrateurs du SI Fourrière pour annuler cette IC.`,
      );
    }
  }

  const warningMessage = {
    title: 'Conséquence de la modification',
    detail: warningDetails,
  };

  const {
    submit,
    inProgress,
    validateUpdateDonneesVehicule,
    isAdminFonctionnel,
  } = useSubmitUpdateDonneesVehicule(
    controller,
    dossierId,
    setDossier,
    closeModal,
  );

  const [isBrancheAdministrative, setIsBrancheAdministrative] = useState(
    motifMef?.branche?.branche === BrancheDtoBrancheEnum.ADMINISTRATIVE,
  );

  const submitFunction = useCallback(submit, [submit]);

  const validateFields = useCallback(
    async (
      field: UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum,
      formValues: UpdateDonnesVehiculeFormValues,
    ) => {
      const params = buildUpdateDonneesVehiculeDto(formValues, controller);

      const response = await validateUpdateDonneesVehicule(
        field,
        dossierId,
        params,
      );

      const violations = response.updateDonneesVehiculeViolationDtoList ?? [];

      if (violations.length === 0) {
        return Promise.resolve();
      }

      return Promise.reject(violations[0].message);
    },
    [controller, validateUpdateDonneesVehicule, dossierId],
  );

  const getBranche = (motif?: MotifMefDto): string | undefined => {
    return motif?.branche?.branche;
  };

  const motifAdministratifCheckbox: FormInputProps<
    UpdateDonnesVehiculeFormValues,
    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum
  >[] =
    isBrancheAdministrative && !isAdminFonctionnel
      ? [
          {
            required: false,
            name: 'prolongationMefAdministrative',
            label: 'Prolongation de MEF administrative',
            dependencies: ['idCorrelationMotifMef'],
            valuePropName: 'checked',
            render: () => {
              return (
                <Checkbox
                  onClick={_ => {
                    setIsMefProlongee(!isMefProlongee);
                  }}
                />
              );
            },
          },
        ]
      : [];

  const motifAdministratifInputs: FormInputProps<
    UpdateDonnesVehiculeFormValues,
    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum
  >[] =
    isMefProlongee && isBrancheAdministrative
      ? [
          {
            field:
              UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_DATE_PROLONGATION_MEF,
            required: true,
            name: 'dateProlongationMef',
            label: 'Date de décision du procureur',
            render: () => {
              return <BaseDatePicker />;
            },
          },
          {
            field:
              UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.COMMENT_PROLONGATION_MEF,
            required: true,
            name: 'commentProlongationMef',
            label: 'Justification',
            render: () => {
              return <BaseTextArea />;
            },
          },
        ]
      : [];

  return (
    <AsyncComponent
      render={() => {
        return dossierBody ? (
          <>
            <h3>
              Mettre à jour des données du véhicule et de l&apos;infraction
            </h3>
            <BaseForm
              initialValues={initialValues}
              onSubmit={submitFunction}
              onCancel={closeModal}
              isSubmitting={inProgress}
              onChange={(_changed, all) => {
                if (all.sansPlaque) {
                  all.plaqueImmatriculation = undefined;
                }
                if (
                  all.plaqueImmatriculation &&
                  all.plaqueImmatriculation !==
                    initialValues.plaqueImmatriculation
                ) {
                  setPlaqueChanged(true);
                } else {
                  setPlaqueChanged(false);
                }
                return all;
              }}
              inputs={[
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_MOTIF_ENTREE_ID_CORRELATION,
                  required: true,
                  name: 'idCorrelationMotifMef',
                  label: 'Motivation de la mesure',
                  dependencies: [
                    'plaqueImmatriculation',
                    'sansPlaque',
                    'paysEtranger',
                    'idCorrelationEtatVehicule',
                  ],
                  render: () => {
                    return (
                      <AsyncSelect
                        fetchOptions={() => {
                          return fetchMotifMefList(refController);
                        }}
                        getOptionValue={(motif: MotifMefDto) => {
                          return motif.idCorrelation;
                        }}
                        getOptionLabel={(motif: MotifMefDto) => {
                          return formatMotifMefDto(motif);
                        }}
                        showSearch={true}
                        onSelect={(
                          _: RawValueType | LabelInValueType,
                          option: BaseOptionType<MotifMefDto>,
                        ) => {
                          setIsBrancheAdministrative(
                            getBranche(option.option) ===
                              BrancheDtoBrancheEnum.ADMINISTRATIVE,
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_ETAT_ID_CORRELATION,
                  required: isAdminFonctionnel,
                  name: 'idCorrelationEtatVehicule',
                  label: 'État du véhicule',
                  dependencies: [
                    'plaqueImmatriculation',
                    'sansPlaque',
                    'paysEtranger',
                    'idCorrelationMotifMef',
                  ],
                  render: () => {
                    return (
                      <AsyncSelect
                        fetchOptions={() => {
                          return fetchEtatsVehicule(refController);
                        }}
                        getOptionValue={(etat: EtatDto) => {
                          return etat.idCorrelation;
                        }}
                        getOptionLabel={(etat: EtatDto) => {
                          return etatDtoToString(etat);
                        }}
                        showSearch={true}
                        disabled={!isAdminFonctionnel}
                      />
                    );
                  },
                },
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_IS_SANS_PLAQUE,
                  required: false,
                  name: 'sansPlaque',
                  label: 'Véhicule Sans plaque',
                  dependencies: [
                    'plaqueImmatriculation',
                    'paysEtranger',
                    'idCorrelationMotifMef',
                    'idCorrelationEtatVehicule',
                  ],
                  valuePropName: 'checked',
                  render: () => {
                    return <Checkbox />;
                  },
                },
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_IS_PAYS_ETRANGER,
                  required: false,
                  name: 'paysEtranger',
                  label: 'Pays Etranger',
                  dependencies: [
                    'plaqueImmatriculation',
                    'sansPlaque',
                    'idCorrelationMotifMef',
                    'idCorrelationEtatVehicule',
                  ],
                  valuePropName: 'checked',
                  render: () => {
                    return <Checkbox />;
                  },
                },
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.NEW_PLAQUE_IMMATRICULATION,
                  required: values => {
                    return !values.sansPlaque;
                  },
                  disabled: values => {
                    return values.sansPlaque === true;
                  },
                  name: 'plaqueImmatriculation',
                  label: "Numéro d'identification ou d'immatriculation",
                  dependencies: [
                    'paysEtranger',
                    'sansPlaque',
                    'idCorrelationMotifMef',
                    'idCorrelationEtatVehicule',
                  ],
                  render: ({ disabled }) => {
                    return <BaseInput disabled={disabled} />;
                  },
                },
                {
                  field:
                    UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum.COMMENT,
                  required: true,
                  name: 'comment',
                  label: 'Justification',
                  render: () => {
                    return <BaseTextArea />;
                  },
                },
                ...motifAdministratifCheckbox,
                ...motifAdministratifInputs,
              ]}
              validateField={validateFields}
              warningMessage={warningMessage}
              renderButtonAction={(Submit, Cancel) => {
                return (
                  <div className="UpdateDonneesVehiculeActions">
                    <div className="UpdateAction">{Cancel}</div>
                    <div className="UpdateAction">{Submit}</div>
                  </div>
                );
              }}
            />
          </>
        ) : null;
      }}
      inProgress={fetchInProgress}
      errorOccured={errorOccured}
      reload={fetchDossier}
      errorMessage="Impossible de charger les données du dossier, veuillez réessayer"
    />
  );
}

export default UpdateDonneesVehicule;
